import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../utils/api";
import { useSettingsSection } from "./SettingsContext";
import { SettingsSectionSkeleton } from "./SettingsSkeleton";

function ToggleCard({
  label,
  description,
  checked,
  onToggle,
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full rounded-xl border px-4 py-4 text-left transition-colors ${
        checked
          ? "border-blue-500 bg-blue-50 text-gray-900 dark:border-blue-400 dark:bg-blue-500/10 dark:text-gray-100"
          : "border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-base font-semibold">{label}</div>
          {description ? <div className="mt-1 text-sm opacity-80">{description}</div> : null}
        </div>
        <div
          className={`mt-1 flex h-8 w-14 shrink-0 items-center rounded-full px-1 transition-colors ${
            checked ? "bg-blue-600 dark:bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
          }`}
        >
          <div
            className={`h-6 w-6 rounded-full bg-white shadow transition-transform ${
              checked ? "translate-x-6" : "translate-x-0"
            }`}
          />
        </div>
      </div>
    </button>
  );
}

function PrinterSelectCard({ printer, selected, onToggle, scopeLabel }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full rounded-xl border px-4 py-4 text-left transition-colors ${
        selected
          ? "border-blue-500 bg-blue-50 text-gray-900 dark:border-blue-400 dark:bg-blue-500/10 dark:text-gray-100"
          : "border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-base font-semibold">{printer.name}</div>
          <div className="mt-1 break-all text-sm opacity-80">{printer.macAddress}</div>
          <div className="mt-2 text-xs font-medium uppercase tracking-wide opacity-70">
            {scopeLabel}
          </div>
        </div>
        <div
          className={`flex h-10 min-w-[88px] items-center justify-center rounded-full px-3 text-sm font-semibold transition-colors ${
            selected
              ? "bg-blue-600 text-white dark:bg-blue-500"
              : "bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-100"
          }`}
        >
          {selected ? "Selected" : "Tap to Select"}
        </div>
      </div>
    </button>
  );
}

export default function PrintingSettings({ stationId }) {
  const [enablePrintingFromOds, setEnablePrintingFromOds] = useState(false);
  const [printTicketOnBump, setPrintTicketOnBump] = useState(false);
  const [printCourseOnBump, setPrintCourseOnBump] = useState(false);
  const [stationMeta, setStationMeta] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinterIds, setSelectedPrinterIds] = useState([]);
  const [showLocationPrinters, setShowLocationPrinters] = useState(false);
  const [loading, setLoading] = useState(true);
  const initialRef = useRef(null);

  const buildValues = useCallback(() => ({
    enablePrintingFromOds: !!enablePrintingFromOds,
    printTicketOnBump: !!printTicketOnBump,
    printCourseOnBump: !!printCourseOnBump,
    selectedPrinterIds: [...selectedPrinterIds].sort((a, b) => a - b),
  }), [enablePrintingFromOds, printTicketOnBump, printCourseOnBump, selectedPrinterIds]);

  useEffect(() => {
    let isMounted = true;

    async function loadSettings() {
      try {
        const [enableRes, ticketRes, courseRes, stationRes, printersRes, selectedPrintersRes] = await Promise.all([
          apiFetch(`/settings/enable_printing_from_ods/${stationId}`),
          apiFetch(`/settings/print_ticket_on_bump/${stationId}`),
          apiFetch(`/settings/print_course_on_bump/${stationId}`),
          apiFetch(`/updates-reports/stations/by-station-id/${stationId}`),
          apiFetch(`/settings/printers`),
          apiFetch(`/settings/selected_printer_ids/${stationId}`),
        ]);

        const [enableData, ticketData, courseData, stationData, printersData, selectedPrintersData] = await Promise.all([
          enableRes.json(),
          ticketRes.json(),
          courseRes.json(),
          stationRes.json(),
          printersRes.json(),
          selectedPrintersRes.json(),
        ]);

        if (!isMounted) return;

        const nextValues = {
          enablePrintingFromOds: !!enableData?.enablePrintingFromOds,
          printTicketOnBump: !!ticketData?.printTicketOnBump,
          printCourseOnBump: !!courseData?.printCourseOnBump,
          selectedPrinterIds: Array.isArray(selectedPrintersData?.selectedPrinterIds)
            ? selectedPrintersData.selectedPrinterIds
                .map((id) => Number(id))
                .filter((id) => Number.isInteger(id) && id > 0)
                .sort((a, b) => a - b)
            : [],
        };

        setEnablePrintingFromOds(nextValues.enablePrintingFromOds);
        setPrintTicketOnBump(nextValues.printTicketOnBump);
        setPrintCourseOnBump(nextValues.printCourseOnBump);
        setSelectedPrinterIds(nextValues.selectedPrinterIds);
        setStationMeta(stationRes.ok ? stationData : null);
        setPrinters(printersRes.ok && Array.isArray(printersData) ? printersData : []);
        initialRef.current = nextValues;
      } catch (err) {
        console.error("Failed to load printing settings:", err);
        const fallback = {
          enablePrintingFromOds: false,
          printTicketOnBump: false,
          printCourseOnBump: false,
          selectedPrinterIds: [],
        };
        if (!isMounted) return;
        setEnablePrintingFromOds(false);
        setPrintTicketOnBump(false);
        setPrintCourseOnBump(false);
        setSelectedPrinterIds([]);
        setStationMeta(null);
        setPrinters([]);
        initialRef.current = fallback;
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadSettings();
    return () => {
      isMounted = false;
    };
  }, [stationId]);

  const saveToStation = useCallback(async (targetStationId, shouldUpdateInitial, overrideValues) => {
    const values = overrideValues || buildValues();

    await Promise.all([
      apiFetch(`/settings/enable_printing_from_ods/${targetStationId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enablePrintingFromOds: values.enablePrintingFromOds }),
      }),
      apiFetch(`/settings/print_ticket_on_bump/${targetStationId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ printTicketOnBump: values.printTicketOnBump }),
      }),
      apiFetch(`/settings/print_course_on_bump/${targetStationId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ printCourseOnBump: values.printCourseOnBump }),
      }),
      apiFetch(`/settings/selected_printer_ids/${targetStationId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ selectedPrinterIds: values.selectedPrinterIds }),
      }),
    ]);

    if (shouldUpdateInitial) {
      initialRef.current = { ...values };
    }
  }, [buildValues]);

  const getChanges = useCallback(() => {
    const initial = initialRef.current;
    if (!initial) return [];

    const current = buildValues();
    const changes = [];

    if (initial.enablePrintingFromOds !== current.enablePrintingFromOds) {
      changes.push(
        `Enable Printing from ODS: ${initial.enablePrintingFromOds ? "On" : "Off"} -> ${current.enablePrintingFromOds ? "On" : "Off"}`
      );
    }
    if (initial.printTicketOnBump !== current.printTicketOnBump) {
      changes.push(
        `Print Ticket On Bump: ${initial.printTicketOnBump ? "On" : "Off"} -> ${current.printTicketOnBump ? "On" : "Off"}`
      );
    }
    if (initial.printCourseOnBump !== current.printCourseOnBump) {
      changes.push(
        `Print Course on Bump: ${initial.printCourseOnBump ? "On" : "Off"} -> ${current.printCourseOnBump ? "On" : "Off"}`
      );
    }
    if (JSON.stringify(initial.selectedPrinterIds || []) !== JSON.stringify(current.selectedPrinterIds || [])) {
      changes.push("Selected Printers updated");
    }

    return changes;
  }, [buildValues]);

  const sublocationPrinters = useMemo(() => {
    const sublocationId = Number(stationMeta?.sublocation_id);
    if (!Number.isFinite(sublocationId)) return [];

    return printers.filter(
      (printer) => Number(printer?.sublocationId) === sublocationId
    );
  }, [printers, stationMeta]);

  const locationPrinters = useMemo(() => {
    const locationId = Number(stationMeta?.location_id);
    if (!Number.isFinite(locationId)) return [];

    return printers.filter(
      (printer) =>
        Number(printer?.locationId) === locationId &&
        (printer?.sublocationId == null || printer?.sublocationId === "")
    );
  }, [printers, stationMeta]);

  const togglePrinter = useCallback((printerId) => {
    setSelectedPrinterIds((current) =>
      current.includes(printerId)
        ? current.filter((id) => id !== printerId)
        : [...current, printerId]
    );
  }, []);

  useSettingsSection(
    "printing-settings",
    useMemo(
      () => ({
        getChanges,
        save: () => saveToStation(stationId, true),
        applyToStation: (targetStationId) => saveToStation(targetStationId, false),
        applySavedToStation: (targetStationId) => {
          if (!initialRef.current) return Promise.resolve();
          return saveToStation(targetStationId, false, initialRef.current);
        },
      }),
      [getChanges, saveToStation, stationId]
    )
  );

  return (
    <div className="mx-auto w-full max-w-4xl">
      <h3 className="mb-4 text-center text-lg font-semibold text-gray-800 dark:text-gray-200">
        Printing
      </h3>

      {loading ? (
        <SettingsSectionSkeleton rows={4} showHeader={false} />
      ) : (
        <div className="space-y-3">
          <ToggleCard
            label="Enable Printing from ODS"
            description="Master toggle for ODS printing features."
            checked={enablePrintingFromOds}
            onToggle={() => setEnablePrintingFromOds((prev) => !prev)}
          />
          <ToggleCard
            label="Print Ticket On Bump"
            description="Skeleton setting only for now. Printing behavior will be added later."
            checked={printTicketOnBump}
            onToggle={() => setPrintTicketOnBump((prev) => !prev)}
          />
          <ToggleCard
            label="Print Course on Bump"
            description="Skeleton setting only for now. Printing behavior will be added later."
            checked={printCourseOnBump}
            onToggle={() => setPrintCourseOnBump((prev) => !prev)}
          />

          <div className="rounded-2xl border border-gray-300 bg-white p-4 dark:border-gray-600 dark:bg-gray-700">
            <div className="mb-3">
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Printer Selection
              </div>
              <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Choose which printers this screen should print to.
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-3 dark:border-blue-500/30 dark:bg-blue-500/10">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Sublocation Printers
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      {stationMeta?.sublocation_name
                        ? `For ${stationMeta.sublocation_name}`
                        : "No sublocation loaded"}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {sublocationPrinters.length ? (
                    sublocationPrinters.map((printer) => (
                      <PrinterSelectCard
                        key={printer.id}
                        printer={printer}
                        selected={selectedPrinterIds.includes(printer.id)}
                        onToggle={() => togglePrinter(printer.id)}
                        scopeLabel="Sublocation Printer"
                      />
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-blue-200 bg-white/70 px-4 py-4 text-sm text-gray-600 dark:border-blue-500/30 dark:bg-gray-800/60 dark:text-gray-300">
                      No sublocation printers are available for this screen yet.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-gray-300 bg-gray-50/80 p-3 dark:border-gray-600 dark:bg-gray-800/60">
                <button
                  type="button"
                  onClick={() => setShowLocationPrinters((prev) => !prev)}
                  className="flex w-full items-center justify-between gap-3 rounded-lg px-1 py-1 text-left"
                >
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Location Printers
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      {stationMeta?.location_name
                        ? `Shared across ${stationMeta.location_name}`
                        : "No location loaded"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm dark:bg-gray-700 dark:text-gray-200">
                      {showLocationPrinters ? "Expanded" : "Collapsed"}
                    </div>
                    <div className="text-lg text-gray-500 dark:text-gray-300">
                      {showLocationPrinters ? "−" : "+"}
                    </div>
                  </div>
                </button>

                {showLocationPrinters ? (
                  <div className="mt-3 space-y-3">
                    {locationPrinters.length ? (
                      locationPrinters.map((printer) => (
                        <PrinterSelectCard
                          key={printer.id}
                          printer={printer}
                          selected={selectedPrinterIds.includes(printer.id)}
                          onToggle={() => togglePrinter(printer.id)}
                          scopeLabel="Location Printer"
                        />
                      ))
                    ) : (
                      <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-4 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        No location-level printers are available for this screen yet.
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
